<template>
    <v-container fluid>
        <v-card>
            <v-overlay :value="loading" absolute>
                <div class="text-center">
                    <v-progress-circular
                        indeterminate
                        color="primary"
                        :size="50"
                        class="my-4"
                    ></v-progress-circular>
                    <p class="display-1">{{ $t("Loading") }}</p>
                </div>
            </v-overlay>
            <v-card-title align-center class="py-3">{{ title }}</v-card-title>
            <v-card-text class="px-5">
                <div class="export-config_header-buttons">
                    <v-menu bottom right>
                        <template v-slot:activator="{ on: menu, attrs }">
                            <v-tooltip top>
                                <template v-slot:activator="{ on: tooltip}">
                                    <v-btn text small v-bind="attrs" v-on="{ ...tooltip, ...menu }" color="blue-grey">
                                        <v-icon large>mdi-history</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ $t('Versions') }}</span>
                            </v-tooltip>
                        </template>
                        <v-list>
                            <v-list-item v-for="(item, i) in versionList" :key="i" @click="onVersionChange(item)">
                                <v-list-item-title>{{ `v${item.text}` }}</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-btn color="secundary" @click.prevent="close()">
                        {{ $t("Cancel") }}
                    </v-btn>
                    <v-btn color="primary" @click.prevent="saveAndExit">
                        {{ $t("Save") }}
                    </v-btn>
                </div>
                <v-row v-if="exportConfigItems.length === 0" justify="center" align="center">
                    <v-col cols="2" justify="center" align="center">
                        <v-btn
                            tile
                            color="primary"
                            prepend-icon="mdi-plus"
                            outlined
                            @click="addContentTypeExportConfig()"
                            >
                            {{ $t("Add") }}
                        </v-btn>
                    </v-col>
                    </v-row>
                    <div v-for="(item, index) in exportConfigItems" :key="item.uuid">
                        <v-row>
                            <v-col cols="1" align-self="center">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                <v-btn
                                    v-on="on"
                                    tile
                                    large
                                    color="error"
                                    icon
                                    @click="removeContentTypeExportConfig(item.uuid)"
                                >
                                    <v-icon>mdi-delete</v-icon>
                                </v-btn>
                                </template>
                                <span>{{ $t("Delete") }}</span>
                            </v-tooltip>
                            </v-col>
                            <v-col align-self="center">
                            <v-text-field
                                readonly
                                v-model="item.displayConditionStr"
                                :label="$t('Condition')"
                                :append-icon="item.conditionStr == '' ? 'mdi-plus' : 'mdi-pencil'"
                                @click:append="editCondition(item)"
                            ></v-text-field>
                            <v-text-field
                                readonly
                                v-model="item.exportMask"
                                :label="$t('ExportMask')"
                                :append-icon="item.exportMask == '' ? 'mdi-plus' : 'mdi-pencil'"
                                @click:append="editMask(item)"
                                :error-messages="maskErrors(index)"
                            ></v-text-field>
                            </v-col>
                            <v-col align-self="center">
                            <v-text-field
                                :label="$t('Prefix')"
                                v-model="item.exportFileNamePrefix"
                            ></v-text-field>
                            <v-text-field
                                :label="$t('Suffix')"
                                v-model="item.exportFileNameSuffix"
                            ></v-text-field>
                            </v-col>
                            <v-col align-self="center">
                            <v-text-field
                                :label="$t('Extension')"
                                v-model="item.exportFileNameExtension"
                            ></v-text-field>
                            <v-select
                                :label="$t('Encoding')"
                                v-model="item.exportEncoding"
                                :items="encodingList"
                                item-text="text"
                                item-value="id"
                            ></v-select>
                            </v-col>
                            <v-col>
                            <v-switch
                                v-model="item.includeOriginalFile"
                                :label="$t('IncludeDocument')"
                            ></v-switch>
                            <v-switch
                                v-model="item.exportMultipleFiles"
                                :label="$t('MultipleFiles')"
                            ></v-switch>
                            </v-col>
                            <v-col>
                            <v-switch
                                v-model="item.exportToServerLocation"
                                :label="$t('ExportOnServer')"
                            ></v-switch>
                            <v-text-field
                                v-if="item.exportToServerLocation"
                                :label="$t('Path')"
                                v-model="item.exportFilePath"
                            ></v-text-field>
                            </v-col>
                            <v-col>
                            <v-switch
                                v-model="item.exportWithHeaders"
                                :label="$t('ExportWithHeaders')"
                            ></v-switch>
                            </v-col>
                            <v-col cols="1" align-self="center">
                            <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                <v-btn
                                    v-on="on"
                                    tile
                                    large
                                    color="primary"
                                    icon
                                    @click="addContentTypeExportConfig()"
                                >
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                                </template>
                                <span>{{ $t("Add") }}</span>
                            </v-tooltip>
                            </v-col>
                        </v-row>
                    </div>
                    <SearchCriteriaBuilderModal
                        :ref="conditionModalKey"
                        :contentTypeIDs="[contentType.id]"
                        @on-condition-saved="onConditionChanged"
                        :allowEmptyCondition="true"
                    />
                    <ExportConfigMaskModal
                        :ref="exportMaskModalKey"
                        :fieldList="contentType.fields"
                        @on-mask-saved="onMaskChanged"
                    />
            </v-card-text>
        </v-card>
    </v-container>
</template>
<script>
import ExportConfigMaskModal from '@/components/administration/contentType/exportConfig/ExportConfigMaskModal.vue';
import SearchCriteriaBuilderModal from '@/components/search/SearchCriteriaBuilderModal.vue';
import ContentType from '@/ecm/models/contentType/ContentType';
import ContentTypeExportConfig from '@/ecm/models/contentType/ContentTypeExportConfig';
import ContentTypeService from '@/services/ContentTypeService';
import { required } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';
import Utils from '@/ecm/common/Utils';

export default {
    name: 'ExportConfig',
    components: {
        SearchCriteriaBuilderModal,
        ExportConfigMaskModal
    },
    async mounted() {
        this.loading = true;
        await this.loadContentTypeData();
        await Promise.all([
            this.getContentTypeExportConfig(),
            this.getVersions(),
            this.getEncodings()
        ]);
        this.$v.$reset();
        this.loading = false;
    },
    async beforeRouteLeave(to, from, next) {
        const isDirty = JSON.stringify(this.exportConfigItems) !== this.originalConfigItems;
        if (isDirty) {
            try {
                const res = await this.unsavedChangesMessageConfirm();
                if (res === 1) {
                    await this.saveAndExit();
                }
                return next();
            } catch (error) {
                console.error(`Error while saving alert: ${error}`);
                return next(false);
            }
        }
        next();
    },
    data() {
        return {
            contentType: new ContentType(),
            conditionModalOpen: false,
            exportConfigItems: [],
            originalConfigItems: null,
            currentItem: new ContentTypeExportConfig(),
            versionList: [],
            encodingList: [],
            conditionModalKey: 'conditionModalKey',
            exportMaskModalKey: 'exportMaskModalKey',
            loading: false
        };
    },
    mixins: [validationMixin],
    validations: {
        exportConfigItems: {
            $each: {
                exportMask: { required }
            }
        }
    },
    computed: {
        title() {
            return `${this.$t('ExportConfiguration')} - ${this.contentType.name} ${this.contentType.version}`;
        },
        maskModalInstance() {
            return this.$refs[this.exportMaskModalKey];
        },
        conditionModalInstance() {
            return this.$refs[this.conditionModalKey];
        }
    },
    methods: {
        async loadContentTypeData() {
            const id = this.$route.params.id;
            const ctData = await ContentTypeService.GetNameAndVersions(id);

            if (!ctData) {
                Utils.toast.error(`${this.$t('ItemNotFoundException')} (${this.$t('ContentType')}: ${id})`);
                this.close();
            }
            this.contentType = new ContentType();
            this.contentType.id = id;
            this.contentType.name = ctData.name;
            this.contentType.version = `v${ctData.currentVersion}`;
            this.versionList = ctData.versions;
        },
        async unsavedChangesMessageConfirm() {
            return new Promise((resolve) => {
                this.$confirm(this.$t('UnsavedChangesWarning'), {
                    title: this.$t('Confirm'),
                    icon: 'mdi-alert',
                    color: 'warning',
                    buttonTrueText: this.$t('Save'),
                    buttonFalseText: this.$t('Cancel')
                }).then((res) => {
                    resolve(res);
                });
            });
        },
        maskErrors(index) {
            const errors = [];
            if (!this.$v.exportConfigItems.$each[index].exportMask.$dirty)
                return errors;
            !this.$v.exportConfigItems.$each[index].exportMask.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        async editCondition(item) {
            this.currentItem = item;
            await this.conditionModalInstance.edit(item.conditionStr);
        },
        onConditionChanged(condition) {
            this.currentItem.conditionStr = condition.conditionStr;
            this.currentItem.displayConditionStr = condition.displayConditionStr;
        },
        async editMask(item) {
            this.currentItem = item;
            await this.maskModalInstance.edit(item.exportMask, this.contentType.id);
        },
        onMaskChanged(mask) {
            this.currentItem.exportMask = mask;
        },
        async onVersionChange(version) {
            if (this.contentType.id === version.id) return;

            this.loading = true;
            const isDirty = JSON.stringify(this.exportConfigItems) !== this.originalConfigItems;
            if (isDirty) {
                const res = await this.unsavedChangesMessageConfirm();
                if (res === 1) {
                    await this.saveContentTypeExportConfig();
                }
                this.$v.$reset();
            }
            this.contentType.id = parseInt(version.id);
            this.contentType.version = `v${version.text}`;
            await Promise.all([this.getVersions(), await this.getContentTypeExportConfig()]);
            this.loading = false;
        },
        async getContentTypeExportConfig() {
            const data = await ContentTypeService.getExportConfig(this.contentType.id);
            if (!data) {
                Utils.toast.error(`${this.$t('ItemNotFoundException')} (${this.$t('ContentType')}: ${this.contentType.id})`);
                this.close();
            }
            this.exportConfigItems = data.map(dto => {
                return new ContentTypeExportConfig(dto);
            });
            if (this.exportConfigItems.length == 0) {
                this.addContentTypeExportConfig();
            }
            this.originalConfigItems = JSON.stringify(this.exportConfigItems);
        },
        async getVersions() {
            const versions = await ContentTypeService.getVersions(this.contentType.id);
            this.versionList = versions;
        },
        async getEncodings() {
            const encondings = await ContentTypeService.getEncodingList();
            this.encodingList = encondings;
        },
        async saveContentTypeExportConfig() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            this.loading = true;
            var exportConfig = this.exportConfigItems.map(item => item.toDto());
            try {
                await ContentTypeService.saveExportConfig(this.contentType.id, exportConfig);
                Utils.toast.success(this.$t('SavedSuccessfully'));
                this.originalConfigItems = JSON.stringify(this.exportConfigItems);
                this.$v.$reset();
            } catch (error) {
                console.error('Error saving content type export config:', error);
            } finally {
                this.loading = false;
            }
        },
        addContentTypeExportConfig() {
            this.exportConfigItems.push(new ContentTypeExportConfig());
        },
        removeContentTypeExportConfig(uuid) {
            this.exportConfigItems = this.exportConfigItems.filter(item => item.uuid !== uuid);
        },
        async saveAndExit() {
            this.$v.$touch();
            if (this.$v.$invalid) return;

            await this.saveContentTypeExportConfig();
            this.close();
        },
        close() {
            this.$router.push({ name: 'ContentTypeList' });
        }
    }
};
</script>
<style>
.export-config_header-buttons {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 5px 0px;
}
</style>
